import { defineStore } from 'pinia';
import { ref } from 'vue';

import { abortApiCall, handleCancelError, refreshAbortController } from '@/apis/axios.utils';
import * as LibraryAPI from '@/apis/library';
import { useAuthStore } from '@/stores/auth';
import { updateList } from '@/utils/updateList';

import { usePopupGalleryStore } from './popup-gallery';

export const useGalleryStore = defineStore(
  'gallery',
  () => {
    const authStore = useAuthStore();
    const popupGalleryStore = usePopupGalleryStore();
    // State
    const pending = ref({
      deleteGallery: false,
      gallery: false,
      galleries: false,
      galleriesPageData: false,
      getGalleriesMultiBrand: false,
      updateGallery: false,
      shoppableGalleries: false,
      galleryMediaV2List: false,
      likeShopFeedMediaV2List: false,
      reorderMedia: false,
      createGalleryStatus: false,
      creationFlow: false,
      addMediaStatus: false,
      removeMediaStatus: false,
      summaryGalleries: 0,
    });

    const error = ref({
      gallery: null,
      createGalleryStatus: null,
      addMediaStatus: null,
      removeMediaStatus: null,
    });

    const abortControllers = {
      getGalleries: ref(null),
      getShoppableGalleries: ref(null),
      getGalleryMediaV2: ref(null),
      getLikeShopFeedMediaV2: ref(null),
      getGalleriesMultiBrand: ref(null),
    };

    const gallery = ref(null);
    const galleries = ref([]);
    const galleriesPageData = ref([]);
    const galleriesMultiBrand = ref({});
    const shoppableGalleries = ref([]);
    const galleriesNextUrl = ref(null);
    const galleriesPageNextUrl = ref(null);
    const shoppableGalleriesNextUrl = ref(null);
    const uniqueImageGroupIds = ref([]);
    const galleryMediaV2List = ref([]);
    const galleryMediaV2ListNext = ref(null);
    const galleryMediaV2ListFullyLoaded = ref(false);
    const likeShopFeedMediaV2List = ref([]);
    const likeShopFeedMediaV2ListNext = ref(null);
    const uniqueLikeShopFeedImageGroupIds = ref([]);
    const likeShopFeedMediaV2ListFullyLoaded = ref(false);
    const newGallery = ref(null);
    const galleriesTotalCount = ref(null);
    const summaryGalleries = ref([]);
    const showShoppableSettingsPopup = ref(false);

    // Actions
    async function getGallery({ galleryId, overrideBrandId = null }) {
      try {
        pending.value.gallery = true;
        const response = await LibraryAPI.getGallery({
          brandId: overrideBrandId ?? authStore.currentBrand.id,
          galleryId,
        });
        const payload = response?.data;
        if (payload) {
          gallery.value = payload;
          updateList(galleries.value, gallery.value);
        }
      } catch (err) {
        gallery.value = null;
        error.value.gallery = err;
        throw err;
      } finally {
        pending.value.gallery = false;
      }
    }

    function clearGallery() {
      gallery.value = null;
    }

    async function getSummaryGalleries({
      type,
      types,
      minSize,
      brandId = authStore.currentBrand?.id,
      ids,
    }) {
      pending.value.summaryGalleries += 1;
      try {
        const response = await LibraryAPI.getGalleries({
          brandId,
          summary: true,
          type,
          types,
          minSize,
          ids,
        });
        const payload = response?.data;
        summaryGalleries.value = payload;
        return payload;
      } finally {
        pending.value.summaryGalleries -= 1;
      }
    }

    function clearSummaryGalleries() {
      summaryGalleries.value = [];
    }

    async function getGalleries({
      type,
      startDate,
      endDate,
      tags,
      tagsFilterType,
      search,
      sort,
      spiritGalleryEnabled,
      limit,
      url,
      ids,
      types,
      pageData = false,
    } = {}) {
      const brandId = authStore.currentBrand?.id;

      pending.value.galleries = true;
      pending.value.galleriesPageData = true;
      let isAxiosCancelError = false;

      try {
        const signal = refreshAbortController(abortControllers.getGalleries);

        const response = await LibraryAPI.getGalleries(
          {
            brandId,
            type,
            startDate,
            endDate,
            tags,
            tagsFilterType,
            search,
            sort,
            spiritGalleryEnabled,
            limit,
            url,
            ids,
            types,
          },
          { signal },
        );
        const payload = response?.data;

        const galleriesData = Array.isArray(payload) ? payload : (payload?.data ?? []);
        galleries.value = [...galleries.value, ...galleriesData];
        galleriesNextUrl.value = payload?.paging?.next;

        if (payload?.paging?.count != null) {
          galleriesTotalCount.value = payload.paging.count;
        }

        if (pageData) {
          galleriesPageData.value = [...galleriesPageData.value, ...galleriesData];
          galleriesPageNextUrl.value = payload?.paging?.next;
        }
      } catch (e) {
        handleCancelError(e, () => {
          isAxiosCancelError = true;
        });
      } finally {
        if (!isAxiosCancelError) {
          pending.value.galleries = false;
          pending.value.galleriesPageData = false;
        }
      }
    }
    function clearGalleries({ pageData = false } = {}) {
      galleries.value = [];
      galleriesNextUrl.value = null;
      galleriesTotalCount.value = null;
      if (pageData) {
        galleriesPageData.value = [];
        galleriesPageNextUrl.value = null;
      }
    }
    function clearGalleriesMultiBrand() {
      galleriesMultiBrand.value = {};
    }

    async function getShoppableGalleries({
      type,
      startDate,
      endDate,
      tags,
      tagsFilterType,
      search,
      sort,
      limit,
      url,
    } = {}) {
      const brandId = authStore.currentBrand?.id;

      pending.value.shoppableGalleries = true;
      let isAxiosCancelError = false;

      try {
        const signal = refreshAbortController(abortControllers.getShoppableGalleries);

        const response = await LibraryAPI.getGalleries(
          {
            brandId,
            type,
            startDate,
            endDate,
            tags,
            tagsFilterType,
            search,
            sort,
            limit,
            url,
            live: 1,
          },
          { signal },
        );
        const payload = response?.data;

        shoppableGalleries.value = [...shoppableGalleries.value, ...(payload?.data ?? [])];
        shoppableGalleriesNextUrl.value = payload?.paging?.next;
      } catch (e) {
        handleCancelError(e, () => {
          isAxiosCancelError = true;
        });
      } finally {
        if (!isAxiosCancelError) {
          pending.value.shoppableGalleries = false;
        }
      }
    }
    function clearShoppableGalleries() {
      shoppableGalleries.value = [];
      shoppableGalleriesNextUrl.value = null;
    }

    async function getGalleryMediaV2({
      galleryId,
      url,
      limit,
      sort,
      include,
      withProductsOnly,
      startDate,
      endDate,
      excludeInstagramOwnedWithoutProducts,
      overrideBrandId,
    }) {
      const brandId = overrideBrandId ?? authStore.currentBrand?.id;

      galleryMediaV2ListFullyLoaded.value = false;
      pending.value.galleryMediaV2List = true;
      try {
        const signal = refreshAbortController(abortControllers.getGalleryMediaV2);

        const response = await LibraryAPI.default.getGalleryMediaV2(
          {
            brandId,
            galleryId,
            limit,
            sort,
            withProductsOnly,
            startDate,
            endDate,
            excludeInstagramOwnedWithoutProducts,
            include,
            url,
          },
          { signal },
        );
        const payload = response?.data;

        const uniqueMediaList =
          payload?.data
            ?.map((item) => {
              if (uniqueImageGroupIds.value.indexOf(item.id) < 0) {
                uniqueImageGroupIds.value.push(item.id);
                return item;
              }
              return null;
            })
            .filter((item) => item !== null) ?? [];
        galleryMediaV2List.value = [...galleryMediaV2List.value, ...uniqueMediaList];
        galleryMediaV2ListNext.value = payload?.paging?.next;
        if (payload?.paging?.next === null) {
          galleryMediaV2ListFullyLoaded.value = true;
        }

        return payload;
      } catch (e) {
        handleCancelError(e);
      } finally {
        pending.value.galleryMediaV2List = false;
      }
      return undefined;
    }

    function clearGalleryMediaV2() {
      galleryMediaV2List.value = [];
      galleryMediaV2ListNext.value = null;
      galleryMediaV2ListFullyLoaded.value = false;
      uniqueImageGroupIds.value = [];
    }

    async function getLikeShopFeedMediaV2({ galleryId, url, limit }) {
      const brandId = authStore.currentBrand?.id;

      likeShopFeedMediaV2ListFullyLoaded.value = false;
      pending.value.likeShopFeedMediaV2List = true;
      try {
        const signal = refreshAbortController(abortControllers.getLikeShopFeedMediaV2);
        const response = await LibraryAPI.default.getGalleryMediaV2(
          {
            brandId,
            galleryId,
            limit,
            sort: 'ORDER',
            withProductsOnly: true,
            excludeInstagramOwnedWithoutProducts: true,
            url,
          },
          { signal },
        );
        const payload = response?.data;

        const uniqueMediaList =
          payload?.data
            ?.map((item) => {
              if (uniqueLikeShopFeedImageGroupIds.value.indexOf(item.id) < 0) {
                uniqueLikeShopFeedImageGroupIds.value.push(item.id);
                return item;
              }
              return null;
            })
            .filter((item) => item !== null) ?? [];
        likeShopFeedMediaV2List.value = [...likeShopFeedMediaV2List.value, ...uniqueMediaList];
        likeShopFeedMediaV2ListNext.value = payload?.paging?.next;
        if (payload?.paging?.next === null) {
          likeShopFeedMediaV2ListFullyLoaded.value = true;
        }

        return payload;
      } catch (e) {
        handleCancelError(e);
      } finally {
        pending.value.likeShopFeedMediaV2List = false;
      }
      return undefined;
    }

    function clearLikeShopFeedMediaV2() {
      likeShopFeedMediaV2List.value = [];
      likeShopFeedMediaV2ListNext.value = null;
      likeShopFeedMediaV2ListFullyLoaded.value = false;
      uniqueLikeShopFeedImageGroupIds.value = [];
    }

    function clearGalleryV2() {
      galleryMediaV2List.value = [];
    }

    function addToFrontOfGalleryMediaList(item) {
      galleryMediaV2List.value.unshift(item);
    }

    function updateGalleryMediaLink({ mediaId, links }) {
      // update target media in the list if there is any
      // galleryMediaList element always has such structure (each element has nested media.id)
      const targetIndex = galleryMediaV2List.value.findIndex((item) => item.id === mediaId);
      if (targetIndex > -1) {
        const linksMap = links.reduce((acc, obj) => {
          acc[obj.url] = obj;
          return acc;
        }, {});
        const targetMedia = galleryMediaV2List.value[targetIndex];
        // Filter list based on the new list
        targetMedia.products =
          targetMedia?.products?.filter((el) => Object.keys(linksMap).includes(el.url)) || [];

        // Adding new links
        const existingUrls = targetMedia.products.map((el) => el.url);
        Object.keys(linksMap).forEach((url) => {
          if (!existingUrls.includes(url)) {
            targetMedia.products.push({ ...linksMap[url], clicks: 0 });
          }
        });
        galleryMediaV2List.value.splice(targetIndex, 1, targetMedia);
      }
    }

    function cancelGetGalleryMediaV2() {
      abortApiCall(abortControllers.getGalleryMediaV2);
    }

    async function reorderMedias({ galleryId, changeList, sort, limit, autoReload = true }) {
      const brandId = authStore.currentBrand?.id;

      pending.value.reorderMedia = true;
      try {
        await LibraryAPI.updateGalleryOrder({ brandId, galleryId, changeList });
      } finally {
        pending.value.reorderMedia = false;
      }

      if (autoReload) {
        clearGalleryMediaV2();
        getGalleryMediaV2({
          galleryId,
          sort,
          limit,
          include: ['predictions'],
        });
      }
    }

    async function createGallery(
      {
        name,
        galleryType,
        duplicate,
        duplicateFilter,
        description,
        tags,
        brandId = authStore.currentBrand?.id,
      },
      creationFlow = false,
    ) {
      error.value.createGalleryStatus = null;
      pending.value.createGalleryStatus = true;
      if (creationFlow) {
        pending.value.creationFlow = true;
      }

      try {
        const response = await LibraryAPI.createGallery({
          brandId,
          name,
          description,
          tags,
          galleryType,
          duplicate,
          duplicateFilter,
        });
        const payload = response?.data;

        newGallery.value = payload;

        return payload;
      } catch (e) {
        error.value.createGalleryStatus = e;
        throw e;
      } finally {
        pending.value.createGalleryStatus = false;
        pending.value.creationFlow = false;
      }
    }

    async function updateGallery({
      galleryId,
      name,
      description,
      tags,
      spiritGalleryEnabled,
      settings,
      tagsUpdated = false,
    }) {
      const brandId = authStore.currentBrand?.id;

      error.value.gallery = null;
      pending.value.gallery = !tagsUpdated;
      pending.value.updateGallery = true;

      try {
        const response = await LibraryAPI.updateGallery(
          {
            brandId,
            galleryId,
            name,
            description,
            tags,
            spiritGalleryEnabled,
            settings,
          },
          { decamelizeRequests: false },
        );
        const payload = response?.data;

        gallery.value = payload;
        updateList(galleries.value, gallery.value);
        updateList(galleriesPageData.value, gallery.value);
        return payload;
      } catch (e) {
        error.value.gallery = e;
        throw e;
      } finally {
        pending.value.gallery = false;
        pending.value.updateGallery = false;
      }
    }

    async function deleteGallery({ galleryId }) {
      pending.value.deleteGallery = true;
      try {
        const brandId = authStore.currentBrand?.id;

        const response = await LibraryAPI.deleteGallery({ brandId, galleryId });

        return response?.data;
      } finally {
        pending.value.deleteGallery = false;
      }
    }

    function clearNewGallery() {
      newGallery.value = null;
    }

    async function addMediaToGallery({ galleryId, mediaIds }, creationFlow = false) {
      const brandId = authStore.currentBrand?.id;

      pending.value.addMediaStatus = true;
      error.value.addMediaStatus = null;

      if (creationFlow) {
        pending.value.creationFlow = true;
      }

      try {
        const response = await LibraryAPI.addGalleryMedia({ brandId, galleryId, mediaIds });
        const payload = response?.data;

        return payload;
      } catch (e) {
        error.value.addMediaStatus = e;
        throw e;
      } finally {
        pending.value.addMediaStatus = false;
        pending.value.creationFlow = false;
      }
    }

    async function removeMediaFromGallery({ galleryId, mediaId }) {
      const brandId = authStore.currentBrand?.id;

      pending.value.removeMediaStatus = true;
      error.value.removeMediaStatus = null;

      try {
        const response = await LibraryAPI.removeGalleryMedia({ brandId, galleryId, mediaId });
        const payload = response?.data;

        // Check if gallery exists or if it's changed. This can happen if a DELETE request was slow
        // and the user navigated away from the board page while it was still pending
        if (gallery.value && gallery.value.id === galleryId) {
          popupGalleryStore.galleryMediaList =
            popupGalleryStore.galleryMediaList?.filter((item) => item.media.id !== mediaId) ?? [];
          gallery.value.gallerySize = popupGalleryStore.galleryMediaList.length;
        }

        return payload;
      } catch (e) {
        error.value.removeMediaStatus = e;
        throw e;
      } finally {
        pending.value.removeMediaStatus = false;
      }
    }

    async function removeBulkMediaFromGallery({ galleryId, mediaIds }) {
      const brandId = authStore.currentBrand?.id;

      pending.value.removeMediaStatus = true;
      error.value.removeMediaStatus = null;

      try {
        const response = await LibraryAPI.removeGalleryMediaBulk({ brandId, galleryId, mediaIds });
        const payload = response?.data;

        // Check if gallery exists or if it's changed. This can happen if a DELETE request was slow
        // and the user navigated away from the board page while it was still pending
        if (gallery.value && gallery.value.id === galleryId) {
          popupGalleryStore.galleryMediaList =
            popupGalleryStore.galleryMediaList?.filter(
              (item) => !mediaIds.includes(item.media.id),
            ) ?? [];
          gallery.value.gallerySize = popupGalleryStore.galleryMediaList.length;
        }

        return payload;
      } catch (e) {
        error.value.removeMediaStatus = e;
        throw e;
      } finally {
        pending.value.removeMediaStatus = false;
      }
    }

    async function getGalleriesMultiBrand({ brandIds, search, types, limit }) {
      pending.value.getGalleriesMultiBrand = true;
      try {
        const signal = refreshAbortController(abortControllers.getGalleriesMultiBrand);
        const response = await LibraryAPI.getGalleriesForBrands(
          { brandIds, search, types, limit },
          { signal },
        );
        galleriesMultiBrand.value = response.data.data;
      } catch (e) {
        handleCancelError(e);
      } finally {
        pending.value.getGalleriesMultiBrand = false;
      }
    }

    function openShoppableSettingsPopup() {
      showShoppableSettingsPopup.value = true;
    }

    function closeShoppableSettingsPopup() {
      showShoppableSettingsPopup.value = false;
    }

    return {
      // State
      pending,
      error,
      abortControllers,
      gallery,
      galleries,
      galleriesPageData,
      galleriesMultiBrand,
      galleriesNextUrl,
      galleriesPageNextUrl,
      shoppableGalleries,
      shoppableGalleriesNextUrl,
      galleryMediaV2List,
      galleryMediaV2ListNext,
      galleryMediaV2ListFullyLoaded,
      likeShopFeedMediaV2List,
      likeShopFeedMediaV2ListNext,
      likeShopFeedMediaV2ListFullyLoaded,
      newGallery,
      galleriesTotalCount,
      showShoppableSettingsPopup,
      // Actions
      getGallery,
      clearGallery,
      getShoppableGalleries,
      summaryGalleries,
      getGalleries,
      getGalleriesMultiBrand,
      clearGalleries,
      clearGalleriesMultiBrand,
      clearShoppableGalleries,
      getGalleryMediaV2,
      clearGalleryMediaV2,
      getLikeShopFeedMediaV2,
      clearGalleryV2,
      clearLikeShopFeedMediaV2,
      addToFrontOfGalleryMediaList,
      updateGalleryMediaLink,
      cancelGetGalleryMediaV2,
      reorderMedias,
      createGallery,
      updateGallery,
      deleteGallery,
      clearNewGallery,
      addMediaToGallery,
      removeMediaFromGallery,
      removeBulkMediaFromGallery,
      getSummaryGalleries,
      clearSummaryGalleries,
      openShoppableSettingsPopup,
      closeShoppableSettingsPopup,
    };
  },
  {
    resetOnBrandChange: true,
  },
);
